import { FormattedMessage, useIntl } from 'react-intl';
import { Facility, Schedule } from '@/features/accounts/types';
import FormattedAppointment from '@/shared/i18n/components/FormattedAppointment';

interface Props {
  schedule: Schedule;
}

interface ScheduleDateRowProps {
  labelId: string;
  at: string | null;
  facility?: Facility | null;
  labelValues?: Record<string, string>;
}

function ScheduleDateRow({ labelId, at, facility, labelValues }: ScheduleDateRowProps) {
  return (
    <div className="space-y-1">
      <span className="block text-xs uppercase tracking-[0.2em] text-slate-400">
        <FormattedMessage id={labelId} values={labelValues} />
      </span>
      <span className="block text-sm font-semibold text-slate-900">
        <FormattedAppointment at={at} />
        {facility && (
          <span className="ml-1 text-xs uppercase tracking-[0.3em] text-slate-400">
            (@ {facility.name})
          </span>
        )}
      </span>
    </div>
  );
}

export default function ScheduleCard({ schedule }: Props) {
  const intl = useIntl();
  const ascLabel = intl.formatMessage({ id: 'schedule.term.asc' });

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/80 p-4 text-sm text-slate-600">
      <span className="text-sm font-semibold text-slate-900">
        <FormattedMessage
          id="accounts.create.confirmation.scheduleLabel"
          values={{ id: schedule.external_id ?? schedule.id }}
        />
      </span>

      <div className="mt-4 space-y-3">
        {schedule.current_asc_at && (
          <ScheduleDateRow
            labelId="schedule.list.asc"
            at={schedule.current_asc_at}
            labelValues={{ asc: ascLabel }}
          />
        )}
        <ScheduleDateRow
          labelId="schedule.list.consular"
          at={schedule.current_consular_at}
          facility={schedule.current_consular_facility}
        />
        {schedule.participants && schedule.participants.length > 0 && (
          <div className="space-y-1">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
              <FormattedMessage id="accounts.create.confirmation.scheduleAttendees" />
            </span>
            <ul className="space-y-1">
              {schedule.participants.map((participant) => (
                <li key={participant.position} className="text-sm text-slate-900">
                  {participant.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
